"use client";

import React, { useEffect, useState } from "react";
import { Play, ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
const testimonials = [{
  id: 1,
  author: "Michael R.",
  position: "Verified Patient | Santa Monica",
  quote: "I haven't slept this well in years. The oral appliance was a complete game-changer for my energy levels and health.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621270/img/testimonial_portrait_jane.png"
}, {
  id: 2,
  author: "Sarah J.",
  position: "Verified Patient | Beverly Hills",
  quote: "No more bulky CPAP! This treatment is comfortable, easy to travel with, and my partner finally sleeps through the night too.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621270/img/testimonial_portrait_mark.png"
}, {
  id: 3,
  author: "Robert T.",
  position: "Verified Patient | Pasadena",
  quote: "The care team in Los Angeles was so professional. They handled my insurance and made the whole process completely stress-free.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621270/img/testimonial_portrait_elena.png"
}, {
  id: 4,
  author: "Linda K.",
  position: "Verified Patient | Burbank",
  quote: "I was skeptical about a dental device for sleep apnea, but the results speak for themselves. My snoring is gone and I feel rested.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621270/img/testimonial_portrait_david.png"
}];
export default function RealLifeResults() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);
  const progressValue = count === 0 ? 0 : current / count * 100;
  return <section data-section-id="99175" id="patient-success-stories" className="dark bg-secondary text-secondary-foreground py-24 md:py-32 overflow-hidden relative">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="mb-12 md:mb-20">
          <span className="text-primary font-sans text-sm tracking-widest uppercase mb-4 block font-bold">
            Patient Success Stories
          </span>
          <h2 className="text-3xl md:text-4xl font-sans font-bold tracking-tight text-white/90">
            Hear from our Los Angeles patients.
          </h2>
        </div>

        <Carousel setApi={setApi} opts={{
        align: "start",
        loop: true
      }} className="w-full">
          <CarouselContent>
            {testimonials.map((testimonial, index) => <CarouselItem key={testimonial.id} data-index={index}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
                  {}
                  <div className="lg:col-span-5 relative group">
                    <Dialog>
                      <DialogTrigger asChild>
                        <div className="relative aspect-[4/5] md:aspect-[3/4] w-full overflow-hidden rounded-2xl cursor-pointer">
                          <img src={testimonial.image} alt={`Portrait of ${testimonial.author}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                          <div className="absolute inset-0 bg-secondary/20 transition-colors duration-500 group-hover:bg-secondary/40" />
                          
                          {}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="size-20 rounded-full bg-primary/30 backdrop-blur-md border border-white/20 flex items-center justify-center text-white transition-transform duration-500 group-hover:scale-110 shadow-xl">
                              <Play className="size-8 ml-1 fill-white" />
                            </div>
                          </div>
                        </div>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden bg-secondary border-white/10">
                        <DialogTitle className="sr-only">
                          Video Testimonial from {testimonial.author}
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                          Watch {testimonial.author} share their experience with our sleep apnea treatment.
                        </DialogDescription>
                        <div className="aspect-video w-full bg-zinc-900 flex items-center justify-center relative">
                          {}
                          <div className="text-center space-y-4">
                            <Play className="size-16 mx-auto text-white/50" />
                            <p className="text-white/50 font-sans text-sm">Patient Video Testimonial</p>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {}
                  <div className="lg:col-span-7 flex flex-col justify-center space-y-8 lg:pl-8">
                    <Quote className="size-12 text-primary/40 rotate-180" />
                    <blockquote className="text-3xl md:text-5xl lg:text-6xl font-sans font-bold leading-[1.1] tracking-tighter text-white">
                      "{testimonial.quote}"
                    </blockquote>
                    <div className="pt-4 border-t border-white/10 inline-block">
                      <div className="font-sans text-lg font-bold text-white">
                        {testimonial.author}
                      </div>
                      <div className="text-white/60 text-sm mt-1 font-sans">
                        {testimonial.position}
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>)}
          </CarouselContent>

          {}
          <div className="mt-16 lg:mt-24 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="w-full md:w-1/3 flex items-center gap-4">
              <span className="text-sm font-sans font-bold text-white/40 w-8 text-right">
                0{current}
              </span>
              <Progress value={progressValue} className="h-1 bg-white/10" />
              <span className="text-sm font-sans font-bold text-white/40 w-8">
                0{count}
              </span>
            </div>

            <div className="flex items-center gap-4">
              <Button variant="outline" size="icon" className="rounded-full size-12 border-primary/30 text-primary hover:bg-primary hover:text-white hover:border-primary transition-colors" onClick={() => api?.scrollPrev()} disabled={!api?.canScrollPrev()} aria-label="Previous testimonial">
                <ArrowLeft className="size-5" />
              </Button>
              <Button variant="outline" size="icon" className="rounded-full size-12 border-primary/30 text-primary hover:bg-primary hover:text-white hover:border-primary transition-colors" onClick={() => api?.scrollNext()} disabled={!api?.canScrollNext()} aria-label="Next testimonial">
                <ArrowRight className="size-5" />
              </Button>
            </div>
          </div>
        </Carousel>
      </div>
    </section>;
}